import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MarsAdmin from '../MarsAdmin';
import MarsFamilyComplete from '../MarsFamilyComplete'; // Ensure this component is imported
import RequesterApp from '../MarsRequestor';
import { loginRequest, resetPassword } from '../authentification/msalConfig.js';
import Logo from '../logo.png';
import { POST_LOGIN } from './ApiDetails.js';
import InformationPopup from './InformationPopup';
import './LoginForm.css';

const Login = () => {

    const { instance } = useMsal();
    const navigate = useNavigate();

    const [loginResponse, setLoginResponse] = useState({ message: '', hasError: false });
    const [showApp, setShowApp] = useState('');  // Store which app to show (Requester, Admin, Family)
    const [showPopup, setShowPopup] = useState(false);  // Control for showing popup
    const [loading, setLoading] = useState(false);

    const setAppToShow = (role) => {
        switch (role) {
            case 'Visit Requester':
                setShowApp('Requester');  // Show RequesterApp in background
                break;
            case 'Admin':
                setShowApp('Admin');  // Show Admin in background
                break;
            case 'Family':
                setShowApp('Family');  // Show Family app in background
                break;
            case 'MLTUser':
                setShowApp('MLTUser');
                break;
            case 'FamilyCoordinator':
                setShowApp('FamilyCoordinator');
                break;
            case 'MLTCoordinator':
                setShowApp('MLTCoordinator');
                break;
            default:
                setShowApp('');
                break;
        }
    }

    const navigateToApp = (role) => {
        // Role-based redirection
        switch (role) {
            case 'Visit Requester':
            case 'Requester':
                navigate('/AppRequester');
                break;
            case 'Admin':
                navigate('/AppAdmin');
                break;
            case 'Family':
                navigate('/AppFamily');
                break;
            case 'MLTUser':
                navigate('/AppMltEvent');
                break;
            case 'FamilyCoordinator':
                navigate('/AppFamily');
                break;
            case 'MLTCoordinator':
                navigate('/AppMltEvent');
                break;
            default:
                navigate('/');  // Redirect to home or login in case of an invalid role
                break;
        }
    }

    // Check if popup is hidden, and automatically navigate if so
    useEffect(() => {
        const storedRole = localStorage.getItem('role');
        const popupSkipped = localStorage.getItem('hidePopup') === 'true';
        if (popupSkipped && storedRole) {
            navigateToApp(storedRole);
        } else {
            if (storedRole === 'null') {
                setLoginResponse({ message: "Your role is not valid. Please try to login or contact your administrator!", hasError: true });
            }
        }
    }, [navigate]);

    const postLogin = async (email, firstName, lastName) => {
        try {
            setLoading(true);
            const response = await axios.post(POST_LOGIN, {
                Email: email,
                FirstName: firstName,
                LastName: lastName,
            });

            setLoading(false);
            setLoginResponse({ message: "Login successful!", hasError: false });

            if (["Family", "MLTUser"].includes(response.data.role) && response.data.assignees.length > 0) {
                localStorage.setItem('assignees', JSON.stringify(response.data.assignees));
            }

            // Store role and username in localStorage
            localStorage.setItem('userId', response.data.userId);
            localStorage.setItem('email', email);
            localStorage.setItem('firstName', firstName);
            localStorage.setItem('lastName', lastName);
            localStorage.setItem('username', response.data.userName);
            localStorage.setItem('role', response.data.role);
            localStorage.setItem('rootRole', response.data.rootRole);

            const queryParams = new URLSearchParams(location.search);
            const returnUrl = queryParams.get('returnUrl');

            setShowPopup(response.data.welcomePopUpIsActive);
            setAppToShow(response.data.role);
            
            returnUrl !== null ? navigate(returnUrl) : navigateToApp(response.data.role);
        } catch (error) {
            setLoading(false);
            if (error.response) {
                setLoginResponse({ message: error.response.data.message || 'Error during login process.', hasError: true });
                console.error('Error response:', error.response.data);
            } else if (error.request) {
                console.error('Error request:', error.request);
                setLoginResponse({ message: 'No response from server', hasError: true });
            } else {
                console.error('Error message:', error.message);
                setLoginResponse({ message: 'Error in request setup', hasError: true });
            }
        }
    }

    const handleLogin = async (event) => {
        event.preventDefault();
        localStorage.clear();
        try {
            const loginResponse = await instance.loginPopup(loginRequest);

            if (loginResponse.account) {
                const email = loginResponse.idTokenClaims.email;
                const lastName = loginResponse.idTokenClaims.family_name;
                const firstName = loginResponse.idTokenClaims.given_name;

                postLogin(email, firstName, lastName);
            }
        } catch (error) {
            if (error.errorMessage && error.errorMessage.includes("AADB2C90118")) {
                await resetPasswordAsync();
                return;
            }
            console.error("Login failed:", error);
            setLoginResponse({ message: 'Login failed', hasError: true });
        }
    };

    const resetPasswordAsync = async () => {
        try {
            const resetResponse = await instance.loginPopup(resetPassword);
            if (resetResponse.account) {
                const email = resetResponse.idTokenClaims.email;
                const lastName = resetResponse.idTokenClaims.family_name;
                const firstName = resetResponse.idTokenClaims.given_name;

                postLogin(email, firstName, lastName);
            }
        } catch (resetError) {
            console.error("Password reset failed:", resetError);
            setLoginResponse({ message: 'Password reset failed', hasError: true });
        }
    }

    const handlePopupClose = (role) => {
        navigateToApp(role);
        // Hide the popup after redirection
        setShowPopup(false);
    };

    return (
        <div className="login-container" style={{ position: 'relative' }}>
            {/* Conditionally show the corresponding app in the background */}
            {showApp === 'Requester' && (
                <div style={{ position: 'absolute', zIndex: 0, top: 0, left: 0, right: 0, bottom: 0 }}>
                    <RequesterApp />
                </div>
            )}
            {showApp === 'Admin' && (
                <div style={{ position: 'absolute', zIndex: 0, top: 0, left: 0, right: 0, bottom: 0 }}>
                    <MarsAdmin />
                </div>
            )}
            {showApp === 'Family' && (
                <div style={{ position: 'absolute', zIndex: 0, top: 0, left: 0, right: 0, bottom: 0 }}>
                    <MarsFamilyComplete
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 0,  // Ensure MarsFamilyComplete is behind the popup
                        }}
                        mapProps={{
                            style: {  // Ensure the map is full-screen in the background
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 0,
                            },
                        }}
                        leftContainerEvent={{
                            style: {  // Ensure leftContainerEvent is correctly placed
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                zIndex: 0,
                            },
                        }}
                    />
                </div>
            )}

            {!showApp && (
                <div className="login-form">
                    <div className="logo-container">
                        <img src={Logo} alt="Logo" className="logo" />
                    </div>
                    <div className="row" style={{ marginBottom: '1rem' }}>
                        <div className="col-6 login-text">
                            <h4>Touch App</h4>
                        </div>
                        <div className="col-6 login-icone">
                            <i className="fa-solid fa-users-rays"></i>
                        </div>
                    </div>
                    {!loading ? (
                        <button onClick={handleLogin} className="loginBtn">Login</button>
                    ) : (
                        <div>Connecting...</div>
                    )}

                    {loginResponse.message && !loading && (
                        <p className={loginResponse.hasError ? 'error-message' : 'success-message'}>
                            {loginResponse.message}
                        </p>
                    )}
                </div>
            )}

            {/* Show the InformationPopup after successful login, with a higher z-index */}
            {showPopup && (
                <div style={{ position: 'absolute', zIndex: 2 }}>
                    <InformationPopup isOpen={showPopup} role={showApp} onClose={handlePopupClose} />
                </div>
            )}
        </div>
    );
};

export default Login;
